import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Building2, FileText, DollarSign, Package, TrendingUp,
  AlertTriangle, CheckCircle, ArrowRight, Brain, Plus,
  Users, BarChart3, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { KYCBanner } from '@/components/KYCBanner';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BusinessDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dashboard, setDashboard] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data } = await axios.get(`${API}/business/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard(data);
      } catch (err) {
        if (err.response?.status === 403) {
          toast({ title: 'Complete your profile first', variant: 'destructive' });
        }
      } finally {
        setLoading(false);
      }
    };
    axios.get(`${API}/business/platform-metrics`).then(r => setMetrics(r.data)).catch(() => {});
    fetchDashboard();
  }, [token]);

  const stats = dashboard ? [
    { label: 'Total AR Value', value: `$${(dashboard.total_ar_value || 0).toLocaleString()}`, icon: FileText, color: 'text-blue-400' },
    { label: 'Total Assets', value: `$${(dashboard.total_asset_value || 0).toLocaleString()}`, icon: Package, color: 'text-purple-400' },
    { label: 'Borrowing Power', value: `$${(dashboard.max_borrowing_power || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-400' },
    { label: 'Active Loans', value: dashboard.active_loans || 0, icon: TrendingUp, color: 'text-cyan-400' },
  ] : [];

  return (
    <DashboardLayout title="Business Dashboard">
      <div data-testid="business-dashboard">
        <KYCBanner />

        {/* Welcome */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Welcome back!</h2>
          <p className="text-muted-foreground" data-testid="dashboard-usp">
            We help businesses monetize their accounts receivable.
          </p>
        </div>

        {/* Profile Warning */}
        {dashboard && !dashboard.profile_complete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="font-medium text-yellow-300">Complete Your Business Profile</p>
                <p className="text-sm text-yellow-400/70">Verify your EIN and company details to start borrowing.</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/business/profile')}
              className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/30">
              Complete Profile <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="p-5 rounded-xl bg-white/[0.03] border border-white/5"
            >
              <stat.icon className={`w-5 h-5 ${stat.color} mb-3`} />
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <button onClick={() => navigate('/business/receivables')}
            data-testid="manage-ar-btn"
            className="p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-blue-500/30 transition-colors text-left group">
            <FileText className="w-6 h-6 text-blue-400 mb-3" />
            <h3 className="font-semibold mb-1">Manage Receivables</h3>
            <p className="text-sm text-gray-400">Upload invoices, track aging, pledge as collateral</p>
            <span className="text-xs text-blue-400 mt-2 inline-block group-hover:underline">
              {dashboard?.receivable_count || 0} receivables &rarr;
            </span>
          </button>

          <button onClick={() => navigate('/business/loan-request')}
            data-testid="new-loan-btn"
            className="p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-green-500/30 transition-colors text-left group">
            <Plus className="w-6 h-6 text-green-400 mb-3" />
            <h3 className="font-semibold mb-1">Request a Loan</h3>
            <p className="text-sm text-gray-400">Pledge AR and assets for a 30-60 day bridge loan</p>
            <span className="text-xs text-green-400 mt-2 inline-block group-hover:underline">
              {dashboard?.active_requests || 0} active requests &rarr;
            </span>
          </button>

          <button onClick={() => navigate('/business/ai-analysis')}
            data-testid="ai-analysis-btn"
            className="p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-purple-500/30 transition-colors text-left group">
            <Brain className="w-6 h-6 text-purple-400 mb-3" />
            <h3 className="font-semibold mb-1">AI Credit Analysis</h3>
            <p className="text-sm text-gray-400">Get an AI-powered assessment of your business health</p>
            <span className="text-xs text-purple-400 mt-2 inline-block group-hover:underline">
              Powered by Gemini &rarr;
            </span>
          </button>
        </div>

        {/* Collateral Summary */}
        {dashboard && (dashboard.total_ar_value > 0 || dashboard.total_asset_value > 0) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="p-6 rounded-xl bg-white/[0.03] border border-white/5"
          >
            <h3 className="font-semibold mb-4">Collateral Overview</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Accounts Receivable</span>
                <span className="font-medium">${(dashboard.total_ar_value || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Tangible Assets</span>
                <span className="font-medium">${(dashboard.total_asset_value || 0).toLocaleString()}</span>
              </div>
              <div className="border-t border-white/5 pt-3 flex justify-between items-center">
                <span className="text-gray-400">Total Collateral</span>
                <span className="font-bold text-lg">${(dashboard.total_collateral || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Max Borrowing Power (90%)</span>
                <span className="font-bold text-green-400">${(dashboard.max_borrowing_power || 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Platform Metrics */}
        {metrics && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="p-6 rounded-xl bg-gradient-to-br from-blue-500/[0.06] to-purple-500/[0.06] border border-blue-500/10 mb-8"
            data-testid="platform-metrics-widget"
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400">Platform Metrics</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Verified Businesses', value: metrics.verified_businesses, icon: Shield, color: 'text-green-400' },
                { label: 'Invoices Tracked', value: metrics.total_invoices_tracked, icon: FileText, color: 'text-blue-400' },
                { label: 'Total AR on Platform', value: `$${(metrics.total_ar_on_platform || 0).toLocaleString()}`, icon: DollarSign, color: 'text-cyan-400' },
                { label: 'Loans Funded', value: metrics.funded_loan_count, icon: TrendingUp, color: 'text-purple-400' },
              ].map((m, i) => (
                <div key={m.label} className="flex items-start gap-3">
                  <m.icon className={`w-4 h-4 ${m.color} mt-0.5 shrink-0`} />
                  <div>
                    <div className="text-lg font-bold">{m.value}</div>
                    <div className="text-xs text-gray-500">{m.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {dashboard && dashboard.total_ar_value === 0 && dashboard.total_asset_value === 0 && dashboard.profile_complete && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-16">
            <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Get Started</h3>
            <p className="text-gray-400 mb-6">Add your accounts receivable and assets to unlock borrowing power.</p>
            <Button onClick={() => navigate('/business/receivables')} className="bg-gradient-to-r from-blue-500 to-purple-500">
              <FileText className="w-4 h-4 mr-2" /> Add Receivables
            </Button>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
